import React, { useEffect } from 'react'
import ButtonBase from '@material-ui/core/ButtonBase'
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined'
import ShowChartIcon from '@material-ui/icons/ShowChart'
import TableChartIcon from '@material-ui/icons/TableChart'
import ListAltIcon from '@material-ui/icons/ListAlt'
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined'
import ScheduleIcon from '@material-ui/icons/Schedule'
import LayersIcon from '@material-ui/icons/Layers'
import MapOutlinedIcon from '@material-ui/icons/MapOutlined'
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined'
import { useAppSettings } from 'Contexts/AppSettingsProvider'
import digitalViewLight from 'assets/img/quick-add-tiles/digital-view-light.webp'
import digitalViewDark from 'assets/img/quick-add-tiles/digital-view-dark.webp'
import chartViewLight from 'assets/img/quick-add-tiles/chart-view-light.webp'
import chartViewDark from 'assets/img/quick-add-tiles/chart-view-dark.webp'
import eventLoggerLight from 'assets/img/quick-add-tiles/event-logger-light.webp'
import eventLoggerDark from 'assets/img/quick-add-tiles/event-logger-dark.webp'
import jobPropertiesLight from 'assets/img/quick-add-tiles/job-properties-light.webp'
import jobPropertiesDark from 'assets/img/quick-add-tiles/job-properties-dark.webp'
import dataTableLight from 'assets/img/quick-add-tiles/data-table-light.webp'
import dataTableDark from 'assets/img/quick-add-tiles/data-table-dark.webp'
import pumpingScheduleLight from 'assets/img/quick-add-tiles/pumping-schedule-light.webp'
import pumpingScheduleDark from 'assets/img/quick-add-tiles/pumping-schedule-dark.webp'
import wellboreViewLight from 'assets/img/quick-add-tiles/wellbore-view-light.webp'
import wellboreViewDark from 'assets/img/quick-add-tiles/wellbore-view-dark.webp'
import loadTemplateLight from 'assets/img/quick-add-tiles/load-template-light.webp'
import loadTemplateDark from 'assets/img/quick-add-tiles/load-template-dark.webp'
import exportToCsvLight from 'assets/img/quick-add-tiles/export-to-csv-light.webp'
import exportToCsvDark from 'assets/img/quick-add-tiles/export-to-csv-dark.webp'
import jobPlannerLight from 'assets/img/quick-add-tiles/job-planner-light.webp'
import jobPlannerDark from 'assets/img/quick-add-tiles/job-planner-dark.webp'
import './QuickAddTiles.styles.scss'

const itemTypeIcons = {
    digitalView: DashboardOutlinedIcon,
    chartView: ShowChartIcon,
    eventLogger: ListAltIcon,
    jobProperties: AssignmentOutlinedIcon,
    dataTable: TableChartIcon,
    pumpingSchedule: ScheduleIcon,
    wellboreData: LayersIcon,
    map: MapOutlinedIcon,
}

// screenshots shown on tile hover, per theme
const itemTypePreviews = {
    digitalView: { light: digitalViewLight, dark: digitalViewDark },
    chartView: { light: chartViewLight, dark: chartViewDark },
    eventLogger: { light: eventLoggerLight, dark: eventLoggerDark },
    jobProperties: { light: jobPropertiesLight, dark: jobPropertiesDark },
    dataTable: { light: dataTableLight, dark: dataTableDark },
    pumpingSchedule: { light: pumpingScheduleLight, dark: pumpingScheduleDark },
    wellboreData: { light: wellboreViewLight, dark: wellboreViewDark },
}

// same for the action tiles, keyed by action id
const actionPreviews = {
    'quickAddTile-loadTemplate': { light: loadTemplateLight, dark: loadTemplateDark },
    'quickAddTile-exportCsv': { light: exportToCsvLight, dark: exportToCsvDark },
    'quickAddTile-jobPlanner': { light: jobPlannerLight, dark: jobPlannerDark },
}

const Tile = ({ id, icon: Icon, label, group = 'newItem', disabled, preview, onClick }) => (
    <ButtonBase
        className={`quickAddTile ${group}${preview ? ' hasPreview' : ''}`}
        disabled={disabled}
        id={id}
        onClick={onClick}
    >
        {
            preview && (
                <span className="quickAddTilePreview" aria-hidden="true">
                    <img src={preview} alt="" draggable={false} />
                </span>
            )
        }
        <span className="quickAddTileIconHolder">
            <Icon className="quickAddTileIcon" />
        </span>
        <span className="quickAddTileName">{label}</span>
        <Icon className="quickAddTileGhostIcon" />
    </ButtonBase>
)

// actions: [{ id, icon, label, group, disabled, onClick }] - rendered as a separate row above the item types,
// group ('options') sets the tile color
const QuickAddTiles = ({ itemTypes, onSelect, actions = [] }) => {
    const { appTheme } = useAppSettings()
    const previewTheme = appTheme === 'Dark' ? 'dark' : 'light'

    // the tiles block renders the logo as its own background, so the page background logo is hidden
    useEffect(() => {
        document.body.classList.add('hasQuickAddTiles')
        return () => document.body.classList.remove('hasQuickAddTiles')
    }, [])

    if (!itemTypes?.length) return null

    return (
        <div className="quickAddTiles">
            <div className="quickAddTilesContent">
                {
                    actions.length > 0 && (
                        <div className="quickAddTilesRow">
                            {
                                actions.map(action => (
                                    <Tile
                                        key={action.id}
                                        preview={actionPreviews[action.id]?.[previewTheme]}
                                        {...action}
                                    />
                                ))
                            }
                        </div>
                    )
                }
                <div className="quickAddTilesRow">
                    {
                        itemTypes.map(item => (
                            <Tile
                                key={item.itemType}
                                id={`quickAddTile-${item.itemType}`}
                                icon={itemTypeIcons[item.itemType] || AddBoxOutlinedIcon}
                                label={item.name}
                                disabled={!item.isAvailable}
                                preview={itemTypePreviews[item.itemType]?.[previewTheme]}
                                onClick={() => onSelect(item.itemType)}
                            />
                        ))
                    }
                </div>
            </div>
        </div>
    )
}

export default QuickAddTiles
